import PropTypes from 'prop-types';
import { useState } from 'react';
import { Box, Skeleton, Typography, Link } from '@mui/material';
import QuadPanel from 'features/IRWorkstation/components/Common/QuadPanel';
import useAccountHealthScores from 'features/IRWorkstation/hooks/useAccountHealthScores';
import { getHealthScoreColor } from 'features/IRWorkstation/utils/healthScoreUtils';
import HealthScoreDrawer from 'features/IRWorkstation/components/HealthScore/HealthScoreDrawer';
import { formatDate } from 'utils/utils';

const SignalRow = ({ signal, onLinkClick }) => {
  const { label, weight, summary, link_to_source } = signal;
  
  const maxLength = 100;
  // Truncate to keep signal summary under 100 chars
  const truncatedSummary = summary?.length > maxLength 
    ? summary.substring(0, maxLength) + '...' 
    : summary;

  
  return (
    <Box
      sx={{
        padding: '10px 15px',
        borderBottom: '0.5px solid',
        borderColor: 'border.light'
      }}>
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', padding: '4px 0' }}>
        <Box component='span' sx={{ fontWeight: 700, color: 'text.primary' }}>
          {label}
        </Box>
        {weight != null && ` - weight ${Math.round(weight)}%`}
      </Typography>
      <Typography sx={{ fontSize: '12px', padding: '4px 0' }}>{truncatedSummary}</Typography>
      {link_to_source && (
        <Link
          href='#'
          onClick={(e) => { e.preventDefault(); onLinkClick?.(signal); }}
          sx={{ cursor: 'pointer' }}
        >
          {link_to_source} -&gt;
        </Link>
      )}
    </Box>
  );
}

SignalRow.propTypes = {
  signal: PropTypes.shape({
    label: PropTypes.string.isRequired,
    weight: PropTypes.number,
    summary: PropTypes.string,
    link_to_source: PropTypes.string,
  }).isRequired,
  onLinkClick: PropTypes.func,
}


const LPHealthScore = ({ accountId }) => {
  const { healthScores, loading, error } = useAccountHealthScores(accountId);

  const classification = healthScores?.classification;
  const classificationColor = getHealthScoreColor(classification);
  // Sort alphabetically by signal name so ordering is consistent across Accounts
  const justifications = [...(healthScores?.justifications ?? [])]
    .sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedHealthScore, setSelectedHealthScore] = useState(null);

	return(
    <Box 
    data-test-id='lp-health-score-card'
    sx={{ width: '100%', minWidth: 0, display: 'flex', '& > *': { flex: 1 } }}
  >

  <QuadPanel title='Relationship Health Score'>

  <Box sx={{
    padding: '9px 14px',
  }}>
    {loading ? (
      <>
        <Skeleton data-testid='lp-health-score-skeleton' variant='text' width='40%' sx={{ fontSize: '12px' }} />
        <Skeleton variant='text' width='55%' sx={{ fontSize: '15px', mb: '2px' }} />
        <Skeleton variant='text' width='50%' sx={{ fontSize: '11px' }} />
      </>
    ) : error ? (
      <Typography color='error.main' sx={{ fontSize: '12px' }}>
        Error loading health score
      </Typography>
    ) : !healthScores ? (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
        No Health Score available yet.
      </Typography>
    ) : (
      <>
        {/* Section label */}
        <Typography sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          mb: '4px',
        }}>
          Classification
        </Typography>
        <Typography sx={{
          fontSize: '15px',
          fontWeight: 700,
          color: classificationColor,
          mb: '2px',
        }}>
          {classification}
        </Typography>
  
        {/* "As of" date subtext */}
        <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
          As of {formatDate(healthScores.date, true)}
        </Typography>
      </>
    )}
  </Box>

    {/* Summary Box  */}
    {(loading || healthScores) && (
      <Box 
      data-test-id='lp-health-score-summary'
      title='Summary' sx={{
        mx: '14px',
        my: '10px',
        border: '0.5px solid',
        borderColor: 'border.light',
        borderRadius: 2,
        p: 2
      }}>
        {loading ? ( 
          <>
            <Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
            <Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
          </>
        ) : (
          <Typography 
            sx={{ 
              fontSize: '12px',
              color: 'text.primary',
              whiteSpace: 'pre-wrap',
              overflowWrap: 'break-word',
            }}>
            {healthScores.summary}
          </Typography>
        )}
      </Box>
    )}

    {/* Signal-Based Justifications */}
    {(loading || healthScores) && (
      <Box 
      data-test-id='lp-health-score-signals'
      sx={{
        padding: '9px 14px'
      }}>
        {loading ? ( 
          <>
            <Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
            <Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
            <Skeleton variant='text' width='55%' sx={{ fontSize: '12px' }} />
          </>
        ) : (
          <Box>
            {justifications.map((signal) => (
              <SignalRow
                key={signal.type}
                signal={signal}
                onLinkClick={(s) => {
                  setSelectedHealthScore(s);
                  setDrawerOpen(true);
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    )}
      {drawerOpen && (
            <HealthScoreDrawer
              open={drawerOpen}
              justification={selectedHealthScore}
              date={healthScores?.date}
              onClose={() => {
                setDrawerOpen(false);
                setSelectedHealthScore(null);
              }}
            />
          )}
	</QuadPanel>
  </Box>
  )
};

LPHealthScore.propTypes = {
  accountId: PropTypes.string.isRequired,
};


export default LPHealthScore;
